import React, { useState, useEffect } from 'react';
import {
  X, Save, Trash2, Plus, ShieldCheck, ShieldOff, CheckCircle, XCircle,
  DollarSign, BookOpen, Clock, Mail, RefreshCw, Edit3, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const BACKEND_URL = window.location.origin;

const PAYMENT_METHODS = ['cash', 'stripe', 'paypal', 'bank_transfer', 'whatsapp', 'admin_granted', 'other'];
const STATUSES = ['completed', 'pending', 'refunded', 'cancelled'];

const methodColors = {
  cash: 'bg-emerald-500/20 text-emerald-300',
  stripe: 'bg-indigo-500/20 text-indigo-300',
  paypal: 'bg-yellow-500/20 text-yellow-300',
  bank_transfer: 'bg-sky-500/20 text-sky-300',
  whatsapp: 'bg-green-500/20 text-green-300',
  admin_granted: 'bg-purple-500/20 text-purple-300',
};
const statusColors = {
  completed: 'bg-green-500/20 text-green-300',
  pending: 'bg-amber-500/20 text-amber-300',
  refunded: 'bg-orange-500/20 text-orange-300',
  cancelled: 'bg-red-500/20 text-red-300',
};

const methodColor = (m) => methodColors[m] || 'bg-gray-500/20 text-gray-300';
const statusColor = (s) => statusColors[s] || 'bg-gray-500/20 text-gray-300';

const fmtDate = (s) => {
  if (!s) return '\u2014';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString();
};

const MethodOptions = () => PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>);
const StatusOptions = () => STATUSES.map((s) => <option key={s} value={s}>{s}</option>);

// ---- single purchase row (extracted to keep parent JSX shallow) ----
const PurchaseRow = ({ p, editing, editRow, setEditRow, onStartEdit, onSaveEdit, onCancelEdit, onDelete }) => {
  if (editing) {
    return (
      <tr className="bg-gray-700/30" data-testid={`purchase-row-${p.id}`}>
        <td className="px-4 py-3">
          <p className="text-white">{p.book_title}</p>
          <p className="text-gray-500 text-xs">{p.book_author}</p>
        </td>
        <td className="px-4 py-3">
          <Input type="number" step="0.01" value={editRow.amount}
            onChange={(e) => setEditRow({ ...editRow, amount: e.target.value })}
            className="bg-gray-700 border-gray-600 text-white text-sm w-24" data-testid="edit-amount" />
        </td>
        <td className="px-4 py-3">
          <select value={editRow.payment_method} onChange={(e) => setEditRow({ ...editRow, payment_method: e.target.value })}
            className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1" data-testid="edit-method">
            <MethodOptions />
          </select>
        </td>
        <td className="px-4 py-3">
          <select value={editRow.status} onChange={(e) => setEditRow({ ...editRow, status: e.target.value })}
            className="bg-gray-700 border border-gray-600 text-white text-xs rounded px-2 py-1" data-testid="edit-status">
            <StatusOptions />
          </select>
        </td>
        <td className="px-4 py-3">
          <Input value={editRow.note} onChange={(e) => setEditRow({ ...editRow, note: e.target.value })}
            className="bg-gray-700 border-gray-600 text-white text-sm" data-testid="edit-note" />
        </td>
        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(p.created_at)}</td>
        <td className="px-4 py-3 text-right whitespace-nowrap">
          <button onClick={onSaveEdit} className="text-green-400 hover:text-green-300 mr-3" data-testid={`save-edit-${p.id}`}><Save className="w-4 h-4 inline" /></button>
          <button onClick={onCancelEdit} className="text-gray-400 hover:text-white"><X className="w-4 h-4 inline" /></button>
        </td>
      </tr>
    );
  }
  return (
    <tr className="hover:bg-gray-700/30" data-testid={`purchase-row-${p.id}`}>
      <td className="px-4 py-3">
        <p className="text-white">{p.book_title}</p>
        <p className="text-gray-500 text-xs">{p.book_author}</p>
      </td>
      <td className="px-4 py-3 text-right text-pink-400 font-semibold whitespace-nowrap">${p.amount.toFixed(2)}</td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 rounded text-xs capitalize ${methodColor(p.payment_method)}`}>{p.payment_method.replace(/_/g, ' ')}</span>
        {p.granted_by_admin && <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] bg-purple-500/20 text-purple-300 uppercase">Manual</span>}
      </td>
      <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs capitalize ${statusColor(p.status)}`}>{p.status}</span></td>
      <td className="px-4 py-3 text-gray-400 text-xs max-w-[160px] truncate">{p.note || '\u2014'}</td>
      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{fmtDate(p.created_at)}</td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button onClick={onStartEdit} className="text-blue-400 hover:text-blue-300 mr-3" title="Edit" data-testid={`edit-purchase-${p.id}`}><Edit3 className="w-4 h-4 inline" /></button>
        <button onClick={onDelete} className="text-red-400 hover:text-red-300" title="Delete / Revoke" data-testid={`delete-purchase-${p.id}`}><Trash2 className="w-4 h-4 inline" /></button>
      </td>
    </tr>
  );
};

// ---- pending request row ----
const PendingRow = ({ p, onActivate, onReject }) => (
  <div className="flex items-center justify-between bg-gray-900/50 rounded-lg px-4 py-2">
    <div>
      <p className="text-white text-sm">{p.book_title}</p>
      <p className="text-gray-500 text-xs">${p.amount.toFixed(2)} · {fmtDate(p.created_at)}</p>
    </div>
    <div className="flex gap-2">
      <Button size="sm" onClick={onActivate} className="bg-green-600 hover:bg-green-700" data-testid={`pending-activate-${p.request_id}`}>
        <CheckCircle className="w-4 h-4 mr-1" /> Activate
      </Button>
      <Button size="sm" variant="outline" onClick={onReject} className="border-red-600 text-red-400" data-testid={`pending-reject-${p.request_id}`}>
        <XCircle className="w-4 h-4 mr-1" /> Reject
      </Button>
    </div>
  </div>
);

const UserDetailModal = ({ userId, allBooks = [], onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({ name: '', email: '', role: 'user', has_access: true });
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newPurchase, setNewPurchase] = useState({ book_id: '', amount: '', payment_method: 'cash', status: 'completed', note: '' });
  const [msg, setMsg] = useState('');

  const api = (path, opts) =>
    fetch(`${BACKEND_URL}/api/admin${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...(opts || {}) });
  const booksApi = (path, opts) =>
    fetch(`${BACKEND_URL}/api/books/admin${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...(opts || {}) });

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const r = await api(`/users/${userId}/detail`);
      if (r.ok) {
        const d = await r.json();
        setData(d);
        setProfile({
          name: d.user.name || '',
          email: d.user.email || '',
          role: d.user.role || 'user',
          has_access: d.user.has_access !== false,
        });
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { if (userId) fetchDetail(); /* eslint-disable-next-line */ }, [userId]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      const r = await api(`/users/${userId}`, { method: 'PUT', body: JSON.stringify(profile) });
      if (r.ok) { flash('Profile saved'); if (onChanged) onChanged(); fetchDetail(); }
      else flash('Failed to save profile');
    } catch (e) { flash('Failed to save profile'); }
    setSaving(false);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditRow({ amount: p.amount, payment_method: p.payment_method, status: p.status, note: p.note || '' });
  };

  const saveEdit = async (p) => {
    setSaving(true);
    try {
      const r = await api(`/users/${userId}/purchases/${p.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          amount: parseFloat(editRow.amount) || 0,
          payment_method: editRow.payment_method,
          status: editRow.status,
          note: editRow.note,
        }),
      });
      if (r.ok) { flash('Purchase updated'); setEditingId(null); fetchDetail(); if (onChanged) onChanged(); }
      else flash('Failed to update purchase');
    } catch (e) { flash('Failed to update purchase'); }
    setSaving(false);
  };

  const deletePurchase = async (p) => {
    if (!window.confirm(`Delete/revoke purchase of "${p.book_title}"? The user will lose access.`)) return;
    setSaving(true);
    try {
      const r = await api(`/users/${userId}/purchases/${p.id}`, { method: 'DELETE' });
      if (r.ok) { flash('Purchase removed'); fetchDetail(); if (onChanged) onChanged(); }
      else flash('Failed to remove purchase');
    } catch (e) { flash('Failed to remove purchase'); }
    setSaving(false);
  };

  const addPurchase = async () => {
    if (!newPurchase.book_id) { flash('Select a book first'); return; }
    setSaving(true);
    try {
      const r = await api(`/users/${userId}/purchases`, {
        method: 'POST',
        body: JSON.stringify({
          book_id: newPurchase.book_id,
          amount: newPurchase.amount === '' ? null : parseFloat(newPurchase.amount),
          payment_method: newPurchase.payment_method,
          status: newPurchase.status,
          note: newPurchase.note,
        }),
      });
      const d = await r.json();
      if (r.ok) {
        flash(d.message || 'Purchase added');
        setShowAdd(false);
        setNewPurchase({ book_id: '', amount: '', payment_method: 'cash', status: 'completed', note: '' });
        fetchDetail(); if (onChanged) onChanged();
      } else flash(d.detail || 'Failed to add purchase');
    } catch (e) { flash('Failed to add purchase'); }
    setSaving(false);
  };

  const handlePending = async (reqId, action) => {
    setSaving(true);
    try {
      const r = await booksApi(`/${action}-purchase/${reqId}`, { method: 'POST' });
      if (r.ok) { flash(action === 'activate' ? 'Purchase activated' : 'Request rejected'); fetchDetail(); if (onChanged) onChanged(); }
      else flash('Action failed');
    } catch (e) { flash('Action failed'); }
    setSaving(false);
  };

  const revokeSessions = async () => {
    if (!window.confirm('Log this user out of all devices?')) return;
    setSaving(true);
    try {
      await api(`/sessions/user/${userId}`, { method: 'DELETE' });
      flash('All sessions revoked'); fetchDetail();
    } catch (e) { flash('Failed'); }
    setSaving(false);
  };

  const sendEmail = async () => {
    setSaving(true);
    try {
      const r = await api(`/users/${userId}/send-welcome-email`, { method: 'POST' });
      const d = await r.json();
      flash(r.ok ? (d.message || 'Email sent') : (d.detail || 'Failed to send email'));
    } catch (e) { flash('Failed to send email'); }
    setSaving(false);
  };

  const avatarInitial = (profile.name || profile.email || '?').charAt(0).toUpperCase();
  const allPending = data ? data.pending_purchases : [];
  const pendingList = allPending.filter((p) => p.status === 'pending');
  const purchaseRows = data ? data.purchases : [];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="user-detail-modal">
      <div className="bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gray-900/95 backdrop-blur border-b border-gray-700 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold text-white">User 360° — Manage & Control</h2>
          <div className="flex items-center gap-3">
            {msg && <span className="text-sm text-pink-300" data-testid="user-detail-flash">{msg}</span>}
            <button onClick={fetchDetail} className="text-gray-400 hover:text-white" title="Refresh" data-testid="user-detail-refresh">
              <RefreshCw className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-white" data-testid="user-detail-close">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {loading || !data ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-gray-800/60 rounded-xl p-5 border border-gray-700">
                <div className="flex items-center gap-4 mb-4">
                  {data.user.picture ? (
                    <img src={data.user.picture} alt="" className="w-16 h-16 rounded-full" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-600 flex items-center justify-center text-2xl text-white">{avatarInitial}</div>
                  )}
                  <div>
                    <p className="text-white font-semibold text-lg">{profile.name || 'No name'}</p>
                    <p className="text-gray-400 text-sm font-mono">{data.user.user_id}</p>
                    <p className="text-gray-500 text-xs">Joined {fmtDate(data.user.created_at)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-400">Name</label>
                    <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white" data-testid="user-edit-name" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Email</label>
                    <Input value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white" data-testid="user-edit-email" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Role</label>
                    <select value={profile.role} onChange={(e) => setProfile({ ...profile, role: e.target.value })}
                      className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-md px-3 py-2" data-testid="user-edit-role">
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Access</label>
                    <button onClick={() => setProfile({ ...profile, has_access: !profile.has_access })}
                      className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm ${profile.has_access ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}
                      data-testid="user-edit-access-toggle">
                      {profile.has_access ? <><ShieldCheck className="w-4 h-4" /> Granted</> : <><ShieldOff className="w-4 h-4" /> Revoked</>}
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4">
                  <Button onClick={saveProfile} disabled={saving} className="bg-pink-600 hover:bg-pink-700" data-testid="user-save-profile-btn">
                    <Save className="w-4 h-4 mr-2" /> Save Profile
                  </Button>
                  <Button onClick={sendEmail} disabled={saving} variant="outline" className="border-blue-600 text-blue-400 hover:bg-blue-600/20" data-testid="user-send-email-btn">
                    <Mail className="w-4 h-4 mr-2" /> Welcome Email
                  </Button>
                  <Button onClick={revokeSessions} disabled={saving} variant="outline" className="border-amber-600 text-amber-400 hover:bg-amber-600/20" data-testid="user-revoke-sessions-btn">
                    Log out all devices
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="bg-gray-800/60 rounded-xl p-4 border border-pink-500/30 flex items-center gap-3">
                  <DollarSign className="w-8 h-8 text-pink-400" />
                  <div>
                    <p className="text-2xl font-bold text-white" data-testid="stat-total-spent">${data.stats.total_spent.toFixed(2)}</p>
                    <p className="text-xs text-gray-400">Total Spent</p>
                  </div>
                </div>
                <div className="bg-gray-800/60 rounded-xl p-4 border border-gray-700 flex items-center gap-3">
                  <BookOpen className="w-8 h-8 text-indigo-400" />
                  <div>
                    <p className="text-2xl font-bold text-white" data-testid="stat-books-owned">{data.stats.books_owned}</p>
                    <p className="text-xs text-gray-400">Books Owned</p>
                  </div>
                </div>
                <div className="bg-gray-800/60 rounded-xl p-4 border border-gray-700 flex items-center gap-3">
                  <Clock className="w-8 h-8 text-amber-400" />
                  <div>
                    <p className="text-2xl font-bold text-white" data-testid="stat-pending-count">{data.stats.pending_count}</p>
                    <p className="text-xs text-gray-400">Pending Requests</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 px-1">
                  {data.sessions.active_count} active session(s) · last seen {fmtDate(data.sessions.last_active)}
                </p>
              </div>
            </div>

            {pendingList.length > 0 && (
              <div className="bg-gray-800/40 rounded-xl border border-amber-500/30 p-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Clock className="w-5 h-5 text-amber-400" /> Pending Purchase Requests</h3>
                <div className="space-y-2">
                  {pendingList.map((p) => (
                    <PendingRow key={p.request_id} p={p}
                      onActivate={() => handlePending(p.request_id, 'activate')}
                      onReject={() => handlePending(p.request_id, 'reject')} />
                  ))}
                </div>
              </div>
            )}

            <div className="bg-gray-800/40 rounded-xl border border-gray-700 overflow-hidden">
              <div className="flex justify-between items-center px-5 py-4 border-b border-gray-700">
                <h3 className="text-white font-semibold">Purchases & Books ({data.purchases.length})</h3>
                <Button onClick={() => setShowAdd(!showAdd)} className="bg-pink-600 hover:bg-pink-700" data-testid="add-purchase-toggle">
                  <Plus className="w-4 h-4 mr-2" /> Add / Grant Book
                </Button>
              </div>

              {showAdd && (
                <div className="bg-gray-900/40 px-5 py-4 grid grid-cols-1 md:grid-cols-6 gap-3 border-b border-gray-700" data-testid="add-purchase-form">
                  <select value={newPurchase.book_id} onChange={(e) => setNewPurchase({ ...newPurchase, book_id: e.target.value })}
                    className="md:col-span-2 bg-gray-700 border border-gray-600 text-white text-sm rounded-md px-3 py-2" data-testid="add-purchase-book">
                    <option value="">Select a book…</option>
                    {allBooks.map((b) => <option key={b.book_id} value={b.book_id}>{b.title}</option>)}
                  </select>
                  <Input type="number" step="0.01" placeholder="Amount (blank = list)" value={newPurchase.amount}
                    onChange={(e) => setNewPurchase({ ...newPurchase, amount: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white text-sm" data-testid="add-purchase-amount" />
                  <select value={newPurchase.payment_method} onChange={(e) => setNewPurchase({ ...newPurchase, payment_method: e.target.value })}
                    className="bg-gray-700 border border-gray-600 text-white text-sm rounded-md px-3 py-2" data-testid="add-purchase-method">
                    <MethodOptions />
                  </select>
                  <select value={newPurchase.status} onChange={(e) => setNewPurchase({ ...newPurchase, status: e.target.value })}
                    className="bg-gray-700 border border-gray-600 text-white text-sm rounded-md px-3 py-2" data-testid="add-purchase-status">
                    <StatusOptions />
                  </select>
                  <Button onClick={addPurchase} disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="add-purchase-submit">Add</Button>
                  <Input placeholder="Note (optional)" value={newPurchase.note}
                    onChange={(e) => setNewPurchase({ ...newPurchase, note: e.target.value })}
                    className="md:col-span-6 bg-gray-700 border-gray-600 text-white text-sm" data-testid="add-purchase-note" />
                </div>
              )}

              {data.purchases.length === 0 ? (
                <p className="text-gray-500 text-center py-10">No purchases yet. Use "Add / Grant Book" to give this user a book.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-900/60 text-gray-400 uppercase text-xs">
                      <tr>
                        <th className="px-4 py-3 text-left">Book</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                        <th className="px-4 py-3 text-left">Method</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Note</th>
                        <th className="px-4 py-3 text-left">Date</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {purchaseRows.map((p) => (
                        <PurchaseRow
                          key={p.id}
                          p={p}
                          editing={editingId === p.id}
                          editRow={editRow}
                          setEditRow={setEditRow}
                          onStartEdit={() => startEdit(p)}
                          onSaveEdit={() => saveEdit(p)}
                          onCancelEdit={() => setEditingId(null)}
                          onDelete={() => deletePurchase(p)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetailModal;
